import { useCallback, useState } from 'react';
import { CreatePDFPages, ExportToExcel } from '../../../../utils/Utils';
import { formatPhone } from '../../../../utils/phoneUtils';
import { GetPatientListExportAPI } from '../../../../api/API';

const EXPORT_FAILED = 'Não foi possível exportar a lista. Tente novamente.';

// Linhas por página do PDF: cada página vira um canvas próprio, o que evita o limite
// de altura do canvas em bases grandes
const ROWS_PER_PDF_PAGE = 25;

function formatDate(isoDate = '') {
  const [year, month, day] = (isoDate || '').split('-');
  return year && month && day ? `${day}/${month}/${year}` : '';
}

function todayStamp() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return {
    file: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    label: `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()}`,
  };
}

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Mesmas colunas que a tabela de pacientes mostra
function toRows(patients) {
  return [...patients]
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'))
    .map((p) => ({
      Nome: p.name || '',
      CPF: p.cpf || '',
      Telefone: p.phone ? formatPhone(p.phone) : '',
      'E-mail': p.email || '',
      Nascimento: formatDate(p.birthday),
      Status: p.status === '1' ? 'Ativo' : 'Inativo',
    }));
}

function buildPdfPage(rows, pageIndex, totalPages, totalPatients, dateLabel) {
  const columns = Object.keys(rows[0] || {});
  const head = columns.map((c) => `<th style="text-align:left;padding:6px 8px;border-bottom:1px solid #E0E0E0;color:#616161;font-weight:600;">${c}</th>`).join('');
  const body = rows.map((row) => `<tr>${columns.map((c) => `<td style="padding:6px 8px;border-bottom:1px solid #F0F0F0;">${escapeHtml(row[c])}</td>`).join('')}</tr>`).join('');

  return `
    <div style="width:794px;padding:40px;box-sizing:border-box;font-family:Arial,sans-serif;font-size:11px;color:#212121;">
      <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:16px;">
        <div style="font-size:18px;font-weight:600;">Pacientes</div>
        <div style="color:#757575;">${totalPatients} pacientes · ${dateLabel} · página ${pageIndex + 1} de ${totalPages}</div>
      </div>
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr>${head}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </div>`;
}

// Busca as linhas no servidor, que só entrega a Proprietário(a) e Administrador(a)
function fetchRows(format) {
  return new Promise((resolve, reject) => {
    GetPatientListExportAPI(
      format,
      (data) => {
        if (!data || data.success !== true || !Array.isArray(data.patients)) {
          reject(new Error(data?.error || EXPORT_FAILED));
          return;
        }
        resolve(toRows(data.patients));
      },
      (error) => reject(new Error(error?.error || EXPORT_FAILED))
    );
  });
}

/**
 * Exporta a lista de pacientes (a mesma que a tabela mostra) em Excel ou PDF.
 * As linhas vêm do servidor (patients/export_list.php), que barra a Equipe;
 * o arquivo continua montado no navegador. Cada export devolve
 * { success, error } para a tela mostrar a falha.
 */
export default function usePatientListExport(patients = []) {
  const [exporting, setExporting] = useState(false);

  const run = useCallback(async (format, build) => {
    setExporting(true);
    try {
      const rows = await fetchRows(format);
      await build(rows);
      return { success: true };
    } catch (error) {
      return { success: false, error: error?.message || EXPORT_FAILED };
    } finally {
      setExporting(false);
    }
  }, []);

  const exportExcel = useCallback(() => run('excel', (rows) => {
    const { file } = todayStamp();
    ExportToExcel(rows, 'Pessoas atendidas', `pessoas-atendidas-${file}`);
  }), [run]);

  const exportPdf = useCallback(() => run('pdf', (rows) => {
    const { file, label } = todayStamp();
    const totalPages = Math.max(1, Math.ceil(rows.length / ROWS_PER_PDF_PAGE));
    const pages = [];
    for (let i = 0; i < totalPages; i++) {
      const slice = rows.slice(i * ROWS_PER_PDF_PAGE, (i + 1) * ROWS_PER_PDF_PAGE);
      pages.push(buildPdfPage(slice, i, totalPages, rows.length, label));
    }
    return CreatePDFPages(pages, `pessoas-atendidas-${file}.pdf`);
  }), [run]);

  return { exportExcel, exportPdf, exporting, isEmpty: patients.length === 0 };
}
